import type { Tables } from '#shared/types/db'

/**
 * Only what the browser needs to act as this user. The rest of the row - email,
 * auth0_id, person_id - never leaves the server; see docs/IMPROVEMENTS.md #1.
 */
export type SessionUser = Pick<Tables<'users'>, 'id' | 'role_id'>

/** How long to wait for the row before giving up. */
const DB_USER_TIMEOUT = 10000 // ms

export function useUser() {
  const { user } = useAuth()

  const dbUser = useState<SessionUser>('user')

  watch(() => user.value?.sub, async (id) => {
    if (!id) return
    if (dbUser.value) return

    // Through the API rather than straight to the table: the browser holds the
    // public anon key, so a direct read means anyone can read every user.
    const data = await $fetch('/api/users', { query: { auth0Id: id } })
      .catch(() => null)

    if (!data) return

    dbUser.value = data
  }, {
    immediate: true,
  })

  /**
   * Resolves once the row backing the Auth0 session has loaded.
   *
   * This used to poll every 10ms forever, so an authenticated user with no
   * `users` row - a first-time login whose insert never happened - hung the
   * page silently, with the admin middleware awaiting it on every route.
   */
  async function waitForDbUser() {
    try {
      await until(dbUser).toBeTruthy({
        timeout: DB_USER_TIMEOUT,
        throwOnTimeout: true,
      })
    }
    catch {
      throw createError({
        statusCode: 404,
        statusMessage: 'Fant ikke brukeren din. Prøv å logge inn på nytt.',
      })
    }
  }

  return {
    user,
    dbUser,
    waitForDbUser,
  }
}
